import { absolutize, ConnectError, ConsoleRequestLogger, generateSpeleoId, getRpcPathFromStrings, parseResponse, RpcLoggerFactory, } from '../utils';
export function defaultUrl(derivedUrl, _, __) {
    return derivedUrl;
}
export function requestImpl({ urlForMethod, additionalHeaders, sendSpeleoTraceId, signal, }) {
    return async (service, method, data) => {
        const url = urlForMethod(getRpcPathFromStrings(service, method), service, method);
        const headers = {};
        headers['Accept'] = 'application/x-protobuf';
        headers['Content-Type'] = 'application/x-protobuf';
        for (const [key, value] of (additionalHeaders instanceof Function
            ? additionalHeaders()
            : additionalHeaders).entries()) {
            headers[key] = value;
        }
        if (sendSpeleoTraceId && !('X-SPELEO-TRACE-ID' in headers)) {
            headers['X-SPELEO-TRACE-ID'] = generateSpeleoId();
        }
        const fetchOptions = {
            method: 'POST',
            credentials: 'include',
            headers,
            body: data,
            signal,
        };
        // why are we absolutizing the url? because in order for node-fetch to work
        // (used during testing and with node.js), the url cannot be relative.
        const response = await fetch(absolutize(url), fetchOptions);
        if (response.status >= 400 ||
            response.headers.get('content-type') !== headers['Content-Type']) {
            const text = await response.clone().text();
            return Promise.reject(new ConnectError(response, null, text));
        }
        return parseResponse(response);
    };
}
export function beforeRequestImpl({ urlForMethod, logger, beforeRequest, }) {
    return (service, method, request) => {
        const url = urlForMethod(getRpcPathFromStrings(service, method), service, method);
        logger.logRequest(url, request);
        if (beforeRequest) {
            beforeRequest(request, service, method);
        }
    };
}
export function handleErrorImpl({ urlForMethod, logger, handleError, }) {
    return (service, method, error) => {
        const url = urlForMethod(getRpcPathFromStrings(service, method), service, method);
        logger.logError(url, error);
        const responseError = new Error(`${error.message} \n Proto: ${method} \n URL: ${url}`, 
        // remove \`@ts-expect-error` once jest supports ES2022
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        { cause: error });
        if (handleError) {
            const modifiedError = handleError(error, service, method);
            if (modifiedError) {
                return modifiedError;
            }
        }
        // we always need to return an error, so if the user didn't
        // perform additional massaging of the error, we return our one.
        return responseError;
    };
}
export function afterResponseImpl({ urlForMethod, logger, afterResponse, }) {
    return (service, method, response) => {
        const url = urlForMethod(getRpcPathFromStrings(service, method), service, method);
        logger.logResponse(url, response);
        if (afterResponse) {
            afterResponse(response, service, method);
        }
    };
}
/**
 * rpc
 * @param urlForMethod - function that takes a derived url and returns a url to make the request to. if not provided, the derived url will be used.
 * @param loggerFactory - factory that returns a logger for each rpc call. we provide two implementations: ConsoleLoggerFactory and SilentRequestLogger, but you can provide your own.
 * @param additionalHeaders - map of additional headers to send with each request. if a function is provided, it will be called before each request to get the headers.
 * @param sendSpeleoTraceId - whether or not to send a speleo trace id with each request
 * @param beforeRequest - callback to run before each request
 * @param afterResponse - callback to run after obtaining a valid response
 * @param handleError - callback to run when an error occurs, either during the request or when parsing the response
 * @param signal - abort signal to pass to fetch
 * @returns rpc implementation that can be used to make rpc calls for any service / method
 */
export default function rpc({ urlForMethod = defaultUrl, loggerFactory = new RpcLoggerFactory(ConsoleRequestLogger), additionalHeaders = new Map(), sendSpeleoTraceId = false, beforeRequest, afterResponse, handleError, signal, }) {
    const logger = loggerFactory.build();
    return {
        request: requestImpl({
            urlForMethod,
            additionalHeaders,
            sendSpeleoTraceId,
            signal,
        }),
        afterResponse: afterResponseImpl({ urlForMethod, logger, afterResponse }),
        beforeRequest: beforeRequestImpl({ urlForMethod, logger, beforeRequest }),
        handleError: handleErrorImpl({ urlForMethod, logger, handleError }),
    };
}
